// Read-only snapshot of the exact content the public landing uses,
// organized by section for the /content-admin panel. Values are pulled
// directly from the same files the landing imports — nothing is duplicated
// by hand here, only labeled and grouped for editing.

import {
  nav,
  hero,
  heroTicker,
  vehicleBanner,
  vehicleBannerPromo,
  stats,
  whyMotorPro,
  carouselSection,
  socialBoost,
  howItWorks,
  whatsappMock,
  whyPublish,
  dealerProgram,
  whyApp,
  logoSection,
  finalCta,
  footer,
  legalPages,
} from "../../content/landingContent";
import { LINKS } from "../../config/links";

import logoHeader from "../../assets/brand/logo-header.png";
import logoFull from "../../assets/brand/logo-full.webp";
import heroComposition from "../../assets/hero/vehicles-composition.jpg";
import appMockup from "../../assets/app/app-mockup.jpg";
import bannerLeft from "../../assets/vehicles/banner-left.jpg";
import bannerRight from "../../assets/vehicles/banner-right.jpg";
import promoMan from "../../assets/vehicles/promo-man.jpg";
import promoWoman from "../../assets/vehicles/promo-woman.jpg";
import promoSecondary from "../../assets/vehicles/promo-secondary.jpg";
import howItWorksStep1 from "../../assets/how-it-works/paso-01.jpg";
import howItWorksStep2 from "../../assets/how-it-works/paso-02.jpg";
import howItWorksStep3 from "../../assets/how-it-works/paso-03.jpg";
import dealerBanner from "../../assets/dealers/banner-autolotes.jpg";
import qrMotorpro from "../../assets/brand/qr-motorpro.png";

export type FieldType = "text" | "textarea" | "url" | "list" | "json" | "boolean" | "color";

export interface AdminField {
  key: string;
  label: string;
  value: string;
  type: FieldType;
  rows?: number;
}

export interface AdminImage {
  key: string;
  label: string;
  path: string;
  src: string;
  recommendedSize: string;
  recommendedFormat: string;
  alt: string;
}

export interface AdminLinkRef {
  label: string;
  value: string;
}

export interface AdminSection {
  id: string;
  title: string;
  filePaths: string[];
  fields: AdminField[];
  images?: AdminImage[];
  linkRefs?: AdminLinkRef[];
  note?: string;
}

const CONTENT_FILE = "src/content/landingContent.ts";
const LINKS_FILE = "src/config/links.ts";

export function getContentSections(): AdminSection[] {
  return [
    {
      id: "header",
      title: "1. Header",
      filePaths: [CONTENT_FILE, "src/components/Header.tsx"],
      fields: [
        { key: "nav.downloadCta", label: "Texto del botón principal", value: nav.downloadCta, type: "text" },
        {
          key: "nav.itemsList",
          label: "Elementos del menú (uno por línea, formato: Etiqueta | #ancla)",
          value: nav.items.map((i) => `${i.label} | ${i.href}`).join("\n"),
          type: "list",
        },
      ],
      images: [
        {
          key: "header.logo",
          label: "Logo del header",
          path: "src/assets/brand/logo-header.png",
          src: logoHeader,
          recommendedSize: "≈600×200 px",
          recommendedFormat: "PNG con fondo transparente",
          alt: "MotorPRO Autos",
        },
      ],
      linkRefs: [{ label: "Descargar App", value: LINKS.appUrl }],
      note: "Cambiar el texto de los anclas (#...) aquí no mueve las secciones reales; para eso edita src/content/landingContent.ts (nav.items) directamente.",
    },
    {
      id: "hero",
      title: "2. Hero",
      filePaths: [CONTENT_FILE],
      fields: [
        { key: "hero.eyebrow", label: "Etiqueta superior", value: hero.eyebrow, type: "text" },
        { key: "hero.title", label: "Título principal (usa saltos de línea)", value: hero.title, type: "textarea" },
        { key: "hero.highlight", label: "Frase de impacto", value: hero.highlight, type: "text" },
        { key: "hero.description", label: "Texto descriptivo", value: hero.description, type: "textarea" },
        { key: "hero.ctaPrimary", label: "Botón primario", value: hero.ctaPrimary, type: "text" },
        { key: "hero.ctaSecondary", label: "Botón secundario", value: hero.ctaSecondary, type: "text" },
      ],
      images: [
        {
          key: "hero.composition",
          label: "Composición de vehículos del Hero",
          path: "src/assets/hero/vehicles-composition.jpg",
          src: heroComposition,
          recommendedSize: "1400×1000 px o superior",
          recommendedFormat: "JPG optimizado",
          alt: "Composición de vehículos disponibles en MotorPRO Autos",
        },
        {
          key: "hero.appMockup",
          label: "Mockup de la app",
          path: "src/assets/app/app-mockup.jpg",
          src: appMockup,
          recommendedSize: "750×1600 px (proporción de teléfono)",
          recommendedFormat: "JPG o PNG",
          alt: "Vista previa de la aplicación MotorPRO",
        },
      ],
      linkRefs: [
        { label: "Descargar App", value: LINKS.appUrl },
        { label: "Explorar vehículos", value: LINKS.exploreUrl },
      ],
    },
    {
      id: "hero-ticker",
      title: "2b. Franja animada (ticker)",
      filePaths: [CONTENT_FILE, "src/components/HeroTicker.tsx"],
      fields: [
        {
          key: "heroTicker.enabled",
          label: "Activar franja animada",
          value: heroTicker.enabled ? "true" : "false",
          type: "boolean",
        },
        {
          key: "heroTicker.textList",
          label: "Texto del ticker (uno por línea, se repite en bucle)",
          value: heroTicker.items.join("\n"),
          type: "list",
        },
        {
          key: "heroTicker.bgColor",
          label: "Color de fondo",
          value: heroTicker.bgColor,
          type: "color",
        },
        {
          key: "heroTicker.textColor",
          label: "Color del texto",
          value: heroTicker.textColor,
          type: "color",
        },
      ],
      note: "Franja ubicada entre el Hero y el Banner automotriz. Ocupa ~40-55px de alto y se desplaza en bucle infinito con CSS (sin la etiqueta <marquee>).",
    },
    {
      id: "banner",
      title: "3. Banner automotriz",
      filePaths: [CONTENT_FILE],
      fields: [
        { key: "vehicleBanner.eyebrow", label: "Etiqueta superior", value: vehicleBanner.eyebrow, type: "text" },
        { key: "vehicleBanner.title", label: "Título principal", value: vehicleBanner.title, type: "text" },
        { key: "vehicleBanner.text", label: "Descripción", value: vehicleBanner.text, type: "textarea" },
        {
          key: "vehicleBanner.benefitsList",
          label: "Beneficios (uno por línea, opcional — se ocultan si queda vacío)",
          value: vehicleBanner.benefits.join("\n"),
          type: "list",
        },
        { key: "vehicleBanner.ctaExplore", label: "Texto del botón rojo (Explorar)", value: vehicleBanner.ctaExplore, type: "text" },
        {
          key: "vehicleBanner.ctaExploreUrl",
          label: "URL del botón rojo (vacío = usar 'Explorar vehículos' de la sección 14)",
          value: vehicleBanner.ctaExploreUrl,
          type: "url",
        },
        { key: "vehicleBanner.ctaPublish", label: "Texto del botón blanco (Publicar)", value: vehicleBanner.ctaPublish, type: "text" },
        {
          key: "vehicleBanner.ctaPublishUrl",
          label: "URL del botón blanco (vacío = usar 'Descargar App' de la sección 14)",
          value: vehicleBanner.ctaPublishUrl,
          type: "url",
        },
        { key: "vehicleBanner.imageAlt1", label: "Alt text — imagen principal", value: vehicleBanner.imageAlt1, type: "text" },
        { key: "vehicleBanner.imageAlt2", label: "Alt text — imagen secundaria", value: vehicleBanner.imageAlt2, type: "text" },
      ],
      images: [
        {
          key: "banner.vehicleLeft",
          label: "Vehículo izquierdo",
          path: "src/assets/vehicles/banner-left.jpg",
          src: bannerLeft,
          recommendedSize: "1000×800 px",
          recommendedFormat: "JPG",
          alt: vehicleBanner.imageAlt1,
        },
        {
          key: "banner.vehicleRight",
          label: "Vehículo derecho",
          path: "src/assets/vehicles/banner-right.jpg",
          src: bannerRight,
          recommendedSize: "1000×800 px",
          recommendedFormat: "JPG",
          alt: vehicleBanner.imageAlt2,
        },
      ],
      linkRefs: [
        { label: "Explorar vehículos (fallback)", value: LINKS.exploreUrl },
        { label: "Publicar mi auto (fallback)", value: LINKS.appUrl },
      ],
      note: "Las URLs de este banner quedan vacías por defecto y usan los enlaces globales de la sección 14 (Footer y enlaces). Complétalas aquí solo si este banner debe apuntar a una URL distinta. Estas 2 imágenes son exclusivas del Banner automotriz — no se comparten con el Carrusel de vehículos ni con la Promoción de lanzamiento.",
    },
    {
      id: "banner-promo",
      title: "3b. Banner — Promoción de lanzamiento (opcional)",
      filePaths: [CONTENT_FILE],
      fields: [
        {
          key: "vehicleBannerPromo.enabled",
          label: "Activar esta promoción en la landing",
          value: vehicleBannerPromo.enabled ? "true" : "false",
          type: "boolean",
        },
        { key: "vehicleBannerPromo.eyebrow", label: "Etiqueta superior", value: vehicleBannerPromo.eyebrow, type: "text" },
        { key: "vehicleBannerPromo.title", label: "Título", value: vehicleBannerPromo.title, type: "textarea" },
        { key: "vehicleBannerPromo.text", label: "Descripción", value: vehicleBannerPromo.text, type: "textarea" },
        {
          key: "vehicleBannerPromo.benefitsList",
          label: "Beneficios (uno por línea)",
          value: vehicleBannerPromo.benefits.join("\n"),
          type: "list",
        },
        { key: "vehicleBannerPromo.ctaPublish", label: "Botón principal (rojo)", value: vehicleBannerPromo.ctaPublish, type: "text" },
        { key: "vehicleBannerPromo.ctaExplore", label: "Botón secundario (blanco)", value: vehicleBannerPromo.ctaExplore, type: "text" },
      ],
      images: [
        {
          key: "promo.man",
          label: "Modelo — Hombre",
          path: "src/assets/vehicles/promo-man.jpg",
          src: promoMan,
          recommendedSize: "1000×800 px",
          recommendedFormat: "JPG",
          alt: "Modelo hombre — Promoción de lanzamiento MotorPRO Autos",
        },
        {
          key: "promo.woman",
          label: "Modelo — Mujer",
          path: "src/assets/vehicles/promo-woman.jpg",
          src: promoWoman,
          recommendedSize: "1000×800 px",
          recommendedFormat: "JPG",
          alt: "Modelo mujer — Promoción de lanzamiento MotorPRO Autos",
        },
        {
          key: "promo.secondary",
          label: "Imagen secundaria (insignia circular)",
          path: "src/assets/vehicles/promo-secondary.jpg",
          src: promoSecondary,
          recommendedSize: "500×500 px (cuadrada)",
          recommendedFormat: "JPG o PNG",
          alt: "Insignia de la promoción de lanzamiento",
        },
      ],
      note: "Mientras 'Activar esta promoción' esté desactivado, la landing sigue mostrando el contenido normal del Banner automotriz (sección 3) sin ningún cambio. Al activarlo, este contenido reemplaza en vivo la etiqueta, título, descripción, beneficios, botones e imágenes del banner. Estas 3 imágenes son exclusivas de la Promoción de lanzamiento — no se comparten con el Banner automotriz ni con el Carrusel de vehículos. Al activar la promoción, 'Modelo — Hombre' y 'Modelo — Mujer' reemplazan las 2 imágenes del banner, y la 'Imagen secundaria' aparece como insignia circular en la esquina.",
    },
    {
      id: "stats",
      title: "4. Estadísticas",
      filePaths: [CONTENT_FILE],
      fields: [
        {
          key: "stats.itemsJson",
          label: "Estadísticas (JSON: prefix, from, to, suffix, label)",
          value: JSON.stringify(stats.items, null, 2),
          type: "json",
        },
      ],
    },
    {
      id: "why-motorpro",
      title: "5. ¿Por qué MotorPRO?",
      filePaths: [CONTENT_FILE],
      fields: [
        { key: "whyMotorPro.title", label: "Título", value: whyMotorPro.title, type: "text" },
        { key: "whyMotorPro.intro", label: "Texto introductorio", value: whyMotorPro.intro, type: "textarea" },
        {
          key: "whyMotorPro.benefitsJson",
          label: "Beneficios (JSON: title, text, icon)",
          value: JSON.stringify(whyMotorPro.benefits, null, 2),
          type: "json",
        },
      ],
    },
    {
      id: "vehicles",
      title: "6. Carrusel de vehículos",
      filePaths: ["src/data/featuredVehicles.ts", "src/assets/vehicles/"],
      fields: [
        { key: "carouselSection.title", label: "Título", value: carouselSection.title, type: "text" },
        { key: "carouselSection.subtitle", label: "Subtítulo", value: carouselSection.subtitle, type: "textarea" },
        { key: "carouselSection.viewAd", label: "Texto botón 'Ver anuncio'", value: carouselSection.viewAd, type: "text" },
        { key: "carouselSection.exploreAll", label: "Texto botón final", value: carouselSection.exploreAll, type: "text" },
      ],
      linkRefs: [{ label: "Explorar todos los vehículos", value: LINKS.exploreUrl }],
      note: "La lista de vehículos se administra en el gestor de abajo (agregar, editar, eliminar, reordenar y exportar).",
    },
    {
      id: "social-boost",
      title: "7. Potencia tus redes",
      filePaths: [CONTENT_FILE],
      fields: [
        { key: "socialBoost.title", label: "Título", value: socialBoost.title, type: "text" },
        { key: "socialBoost.mainText", label: "Texto principal", value: socialBoost.mainText, type: "textarea" },
        { key: "socialBoost.supportText", label: "Texto de apoyo", value: socialBoost.supportText, type: "textarea" },
        {
          key: "socialBoost.benefitsList",
          label: "Beneficios (uno por línea)",
          value: socialBoost.benefits.join("\n"),
          type: "list",
        },
        { key: "socialBoost.compareLive", label: "Etiqueta 'Live'", value: socialBoost.compareLive, type: "text" },
        { key: "socialBoost.compareMotor", label: "Etiqueta 'MotorPRO'", value: socialBoost.compareMotor, type: "text" },
      ],
    },
    {
      id: "how-it-works",
      title: "8. ¿Cómo funciona?",
      filePaths: [CONTENT_FILE],
      fields: [
        { key: "howItWorks.title", label: "Título", value: howItWorks.title, type: "text" },
        { key: "howItWorks.subtitle", label: "Subtítulo", value: howItWorks.subtitle, type: "textarea" },
        {
          key: "howItWorks.stepsJson",
          label: "Pasos (JSON: number, title, text)",
          value: JSON.stringify(howItWorks.steps, null, 2),
          type: "json",
        },
        {
          key: "whatsappMock.messagesJson",
          label: "Conversación de WhatsApp ilustrativa (JSON)",
          value: JSON.stringify(whatsappMock.messages, null, 2),
          type: "json",
        },
      ],
      images: [
        { key: "step-1", label: "Imagen Paso 1", path: "src/assets/how-it-works/paso-01.jpg", src: howItWorksStep1, recommendedSize: "800×600 px", recommendedFormat: "JPG o PNG", alt: "Paso 1" },
        { key: "step-2", label: "Imagen Paso 2", path: "src/assets/how-it-works/paso-02.jpg", src: howItWorksStep2, recommendedSize: "800×600 px", recommendedFormat: "JPG o PNG", alt: "Paso 2" },
        { key: "step-3", label: "Imagen Paso 3", path: "src/assets/how-it-works/paso-03.jpg", src: howItWorksStep3, recommendedSize: "800×600 px", recommendedFormat: "JPG o PNG", alt: "Paso 3" },
      ],
    },
    {
      id: "why-publish",
      title: "9. ¿Por qué publicar?",
      filePaths: [CONTENT_FILE],
      fields: [
        { key: "whyPublish.title", label: "Título", value: whyPublish.title, type: "text" },
        {
          key: "whyPublish.benefitsList",
          label: "Beneficios (uno por línea)",
          value: whyPublish.benefits.join("\n"),
          type: "list",
        },
        { key: "whyPublish.closing", label: "Texto de cierre", value: whyPublish.closing, type: "textarea" },
      ],
    },
    {
      id: "dealers",
      title: "10. Programa para autolotes",
      filePaths: [CONTENT_FILE],
      fields: [
        { key: "dealerProgram.title", label: "Título", value: dealerProgram.title, type: "text" },
        { key: "dealerProgram.subtitle", label: "Subtítulo", value: dealerProgram.subtitle, type: "text" },
        { key: "dealerProgram.mainMessage", label: "Mensaje principal", value: dealerProgram.mainMessage, type: "text" },
        { key: "dealerProgram.text", label: "Texto", value: dealerProgram.text, type: "textarea" },
        {
          key: "dealerProgram.benefitsList",
          label: "Beneficios (uno por línea)",
          value: dealerProgram.benefits.join("\n"),
          type: "list",
        },
        { key: "dealerProgram.cta", label: "Texto del botón WhatsApp", value: dealerProgram.cta, type: "text" },
      ],
      images: [
        { key: "dealers.banner", label: "Banner del programa", path: "src/assets/dealers/banner-autolotes.jpg", src: dealerBanner, recommendedSize: "1200×900 px", recommendedFormat: "JPG o PNG", alt: "Programa para autolotes MotorPRO" },
      ],
      linkRefs: [{ label: "WhatsApp — Programa para autolotes", value: LINKS.whatsappDealers }],
    },
    {
      id: "why-app",
      title: "11. ¿Por qué una aplicación?",
      filePaths: [CONTENT_FILE],
      fields: [
        { key: "whyApp.title", label: "Título", value: whyApp.title, type: "text" },
        { key: "whyApp.text", label: "Texto", value: whyApp.text, type: "textarea" },
        { key: "whyApp.extraText", label: "Texto adicional", value: whyApp.extraText, type: "textarea" },
        {
          key: "whyApp.benefitsList",
          label: "Beneficios (uno por línea)",
          value: whyApp.benefits.join("\n"),
          type: "list",
        },
        { key: "whyApp.closing", label: "Frase final", value: whyApp.closing, type: "textarea" },
      ],
    },
    {
      id: "logo-section",
      title: "12. Sección con fondo del logo",
      filePaths: [CONTENT_FILE],
      fields: [
        { key: "logoSection.title", label: "Título", value: logoSection.title, type: "text" },
        { key: "logoSection.subtitle", label: "Subtítulo", value: logoSection.subtitle, type: "textarea" },
        { key: "logoSection.ctaPrimary", label: "Botón primario", value: logoSection.ctaPrimary, type: "text" },
        { key: "logoSection.ctaSecondary", label: "Botón secundario", value: logoSection.ctaSecondary, type: "text" },
      ],
      images: [
        {
          key: "logoSection.background",
          label: "Fondo visual del logo",
          path: "src/assets/brand/logo-full.webp",
          src: logoFull,
          recommendedSize: "1600×1000 px o superior",
          recommendedFormat: "WEBP o JPG",
          alt: "Identidad visual MotorPRO Autos",
        },
      ],
      linkRefs: [
        { label: "Descargar App", value: LINKS.appUrl },
        { label: "Explorar vehículos", value: LINKS.exploreUrl },
      ],
    },
    {
      id: "final-cta",
      title: "13. CTA final",
      filePaths: [CONTENT_FILE],
      fields: [
        { key: "finalCta.title", label: "Título", value: finalCta.title, type: "text" },
        { key: "finalCta.text", label: "Texto", value: finalCta.text, type: "textarea" },
        { key: "finalCta.ctaPrimary", label: "Botón primario", value: finalCta.ctaPrimary, type: "text" },
        { key: "finalCta.ctaSecondary", label: "Botón secundario", value: finalCta.ctaSecondary, type: "text" },
        { key: "finalCta.qrCaption", label: "Texto bajo el QR", value: finalCta.qrCaption, type: "text" },
        { key: "finalCta.comingSoon", label: "Nota 'Próximamente'", value: finalCta.comingSoon, type: "text" },
      ],
      images: [
        { key: "final-cta.qr", label: "Código QR", path: "src/assets/brand/qr-motorpro.png", src: qrMotorpro, recommendedSize: "400×400 px", recommendedFormat: "PNG", alt: "Código QR MotorPRO Autos" },
      ],
      linkRefs: [
        { label: "Descargar App", value: LINKS.appUrl },
        { label: "Explorar anuncios", value: LINKS.exploreUrl },
      ],
    },
    {
      id: "footer",
      title: "14. Pie de página y contenido legal",
      filePaths: [CONTENT_FILE, LINKS_FILE, "src/components/Footer.tsx", "src/pages/LegalPage.tsx"],
      fields: [
        { key: "footer.domain", label: "Dominio (texto visible)", value: footer.domain, type: "text" },
        { key: "footer.domainUrl", label: "URL del dominio", value: footer.domainUrl, type: "url" },
        { key: "footer.facebookHandle", label: "Nombre de Facebook", value: footer.facebookHandle, type: "text" },
        { key: "links.facebook", label: "URL de Facebook", value: LINKS.facebook, type: "url" },
        { key: "footer.instagramHandle", label: "Nombre de Instagram", value: footer.instagramHandle, type: "text" },
        { key: "links.instagram", label: "URL de Instagram", value: LINKS.instagram, type: "url" },
        { key: "footer.tiktokHandle", label: "Nombre de TikTok", value: footer.tiktokHandle, type: "text" },
        { key: "links.tiktok", label: "URL de TikTok", value: LINKS.tiktok, type: "url" },
        { key: "footer.whatsappNumber", label: "Número de WhatsApp (se muestra tal cual y genera el enlace wa.me)", value: footer.whatsappNumber, type: "text" },
        { key: "footer.rights", label: "Texto de copyright", value: footer.rights, type: "text" },
        { key: "footer.privacy", label: "Texto visible — Política de privacidad", value: footer.privacy, type: "text" },
        { key: "footer.terms", label: "Texto visible — Términos y condiciones", value: footer.terms, type: "text" },
        { key: "links.privacyPolicy", label: "URL — Política de privacidad", value: LINKS.privacyPolicy, type: "url" },
        { key: "links.termsAndConditions", label: "URL — Términos y condiciones", value: LINKS.termsAndConditions, type: "url" },
        { key: "links.appUrl", label: "Descargar App (URL)", value: LINKS.appUrl, type: "url" },
        { key: "links.exploreUrl", label: "Explorar vehículos (URL)", value: LINKS.exploreUrl, type: "url" },
        { key: "links.whatsappGeneral", label: "WhatsApp general — otros CTA (URL wa.me)", value: LINKS.whatsappGeneral, type: "url" },
        { key: "links.whatsappDealers", label: "WhatsApp autolotes (URL wa.me)", value: LINKS.whatsappDealers, type: "url" },
        { key: "legalPages.privacyTitle", label: "Página legal — Título de Política de privacidad", value: legalPages.privacyTitle, type: "text" },
        { key: "legalPages.privacyContent", label: "Página legal — Contenido de Política de privacidad (admite párrafos y saltos de línea)", value: legalPages.privacyContent, type: "textarea", rows: 12 },
        { key: "legalPages.termsTitle", label: "Página legal — Título de Términos y condiciones", value: legalPages.termsTitle, type: "text" },
        { key: "legalPages.termsContent", label: "Página legal — Contenido de Términos y condiciones (admite párrafos y saltos de línea)", value: legalPages.termsContent, type: "textarea", rows: 12 },
      ],
      images: [
        {
          key: "header.logo",
          label: "Logo del footer (mismo archivo que el logo del header)",
          path: "src/assets/brand/logo-header.png",
          src: logoHeader,
          recommendedSize: "≈600×200 px",
          recommendedFormat: "PNG con fondo transparente",
          alt: "MotorPRO Autos",
        },
      ],
      note: "El dominio, redes, WhatsApp y textos legales se muestran en el pie de página (Footer.tsx). Las páginas legales completas viven en /politica-de-privacidad y /terminos-y-condiciones (LegalPage.tsx) y usan el título/contenido definidos aquí.",
    },
  ];
}
